import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui';
import { XStack } from '@/components/ui/layout/layout';

import { AccountBadge } from '../components/account-badge';
import {
  WIKI_PROVIDERS,
  type WikiAccount,
  type WikiPerson,
} from '../people-wiki.types';

import { AddAccountForm } from './add-account-form';
import { useRemoveAccountMutation } from './edits.query';

// A connected app brings back an account that is removed, so only the others can go. That includes a profile that
// a web lookup found, in case it is the wrong person's. A Littlebird account is kept by its connection, whatever its
// source says, and the connection button ends it.
const isRemovable = ({ provider, source }: WikiAccount): boolean =>
  provider !== WIKI_PROVIDERS.littlebird && source !== 'sync';

/** The accounts as badges that can be edited, and the row ends with a button that adds one. */
export const AccountsEditor = ({ person }: { person: WikiPerson }) => {
  const [isAdding, setIsAdding] = useState(false);
  const { mutate: removeAccount } = useRemoveAccountMutation(person.id);

  return (
    <XStack wrap className="items-center gap-1.5">
      {person.accounts.map((account) => (
        <AccountBadge
          key={account.id}
          account={account}
          onRemove={
            isRemovable(account) ? () => removeAccount(account.id) : undefined
          }
        />
      ))}
      {isAdding ? (
        <AddAccountForm person={person} onClose={() => setIsAdding(false)} />
      ) : (
        <Button
          variant="ghost"
          size="xs"
          className="text-foreground-muted"
          onClick={() => setIsAdding(true)}
        >
          <Icons.plus className="size-3" />
          Add
        </Button>
      )}
    </XStack>
  );
};
